import { useState, useEffect, useCallback } from 'react';
import api from '@/utils/api';
import { AIContentRequest } from '@/lib/types';
import { toaster } from '@/components/ui/toaster';

interface UseAIContentRequestsProps {
  workspaceId: string;
  contentTypeId?: string | number;
  limit?: number;
  offset?: number;
  searchQuery?: string;
}

interface AIContentRequestsResponse {
  data: AIContentRequest[];
  total: number;
  limit: number;
  offset: number;
}

const useAIContentRequests = ({
  workspaceId,
  contentTypeId,
  limit = 50,
  offset = 0,
  searchQuery = '',
}: UseAIContentRequestsProps) => {
  const [requests, setRequests] = useState<AIContentRequest[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [total, setTotal] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [reload, setReload] = useState(false);

  const fetchData = useCallback(async () => {
    if (!workspaceId) {
      setRequests([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
        ...(searchQuery && { q: searchQuery }),
      });

      // Use the new endpoint with contentTypeId if provided
      const endpoint = contentTypeId
        ? `ai-content-requests/workspace/${workspaceId}/${contentTypeId}?${params.toString()}`
        : `ai-content-requests/workspace/${workspaceId}?${params.toString()}`;

      const response = await api.get<AIContentRequestsResponse>(endpoint);

      const newRequests = response.data.data || [];

      if (offset === 0) {
        setRequests(newRequests);
      } else {
        setRequests((prevRequests) => [...prevRequests, ...newRequests]);
      }

      setTotal(response.data.total || 0);
      setHasMore(newRequests.length === limit);
      setIsLoading(false);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to fetch AI content requests',
      });
      setIsLoading(false);
    }
  }, [workspaceId, contentTypeId, limit, offset, searchQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData, reload]);

  const reloadRequests = () => {
    setReload((prev) => !prev);
  };

  const loadMore = () => {
    if (!isLoading && hasMore) {
      // This would be handled by the parent component updating offset
    }
  };

  const removeRequest = useCallback((requestId: number) => {
    setRequests((prevRequests) => prevRequests.filter((req) => req.id !== requestId));
    setTotal((prev) => Math.max(0, prev - 1));
  }, []);

  return {
    requests,
    isLoading,
    total,
    hasMore,
    reloadRequests,
    loadMore,
    removeRequest,
  };
};

export default useAIContentRequests;
